// DeployPackageModal — modal for deploying a TriggerWatch package to a single device
// Matches the style/pattern of ScheduleUpdateModal, with an added package selector

import { useState } from "react";

export default function DeployPackageModal({ title, subtitle, packages, onConfirm, onCancel }) {
  const [packageId, setPackageId] = useState(packages.length > 0 ? packages[0].id : "");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("23:00");

  const today = new Date().toISOString().split("T")[0];

  if (packages.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md mx-4">
          <h2 className="text-white font-semibold text-lg mb-2">{title}</h2>
          <p className="text-gray-400 text-sm mb-6">
            No packages available yet. Create one on the TriggerWatch Packages page first.
          </p>
          <button
            onClick={onCancel}
            className="w-full bg-transparent border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white text-sm py-2 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md mx-4">
        <h2 className="text-white font-semibold text-lg mb-1">{title}</h2>
        <p className="text-gray-400 text-sm mb-6">{subtitle}</p>

        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-1">Package</label>
          <select
            value={packageId}
            onChange={e => setPackageId(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
          >
            {packages.map(p => (
              <option key={p.id} value={p.id}>{p.name} v{p.version} ({p.payload_type})</option>
            ))}
          </select>
        </div>

        {/* Option 1 — Run immediately */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4">
          <p className="text-white text-sm font-medium mb-1">Run on next check-in</p>
          <p className="text-gray-400 text-xs mb-3">The agent will download and execute the package the next time it checks in (within 60 seconds).</p>
          <button
            onClick={() => onConfirm(packageId, null)}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium py-2 rounded-lg transition-colors"
          >
            Deploy Now
          </button>
        </div>

        {/* Option 2 — Schedule for later */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4">
          <p className="text-white text-sm font-medium mb-1">Schedule for later</p>
          <p className="text-gray-400 text-xs mb-3">Pick a date and time for the agent to run the deployment.</p>
          <div className="flex gap-3 mb-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-400 mb-1">Date</label>
              <input
                type="date"
                value={scheduledDate}
                min={today}
                onChange={e => setScheduledDate(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-400 mb-1">Time</label>
              <input
                type="time"
                value={scheduledTime}
                onChange={e => setScheduledTime(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          {scheduledDate && (
            <p className="text-gray-500 text-xs mb-3">
              Scheduled for: {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
          <button
            onClick={() => {
              if (!scheduledDate) { alert("Please select a date."); return; }
              onConfirm(packageId, `${scheduledDate}T${scheduledTime}:00`);
            }}
            className="w-full bg-gray-600 hover:bg-gray-500 text-white text-sm font-medium py-2 rounded-lg transition-colors"
          >
            Schedule Deployment
          </button>
        </div>

        {/* Cancel */}
        <button
          onClick={onCancel}
          className="w-full bg-transparent border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white text-sm py-2 rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}